/**
 * api.ts — the one way to call the Cedar API.
 *
 * WHY THIS EXISTS
 * ===============
 * `web/src/CLAUDE.md` has instructed contributors to "use `apiCall()` from
 * `services/api.ts` for all API requests" for a long time. **The function did not
 * exist.** Zero importers, no file. Every contributor who looked for the documented
 * lever found nothing and hand-rolled a `fetch` with a manual Authorization header
 * instead — 314 times, across 122 files.
 *
 * That is not a style problem. A hand-rolled header:
 *   - cannot proactively refresh a JWT that is about to expire,
 *   - cannot retry once after the server rejects it,
 *   - and fires the request even when there is NO credential to send.
 *
 * The last one is the expensive one. Production, 3-day CloudWatch sweep 2026-08-19:
 * `AUTH_401 reason=missing_token` **242** events against `reason=expired` **117**. The
 * dominant rejection is not a stale token — it is a client asking with no credential at
 * all. On 2026-08-16 at 21:08:33 UTC one client fired seven requests in 240ms
 * (hr-zones, goals, plans, workouts, mobility/routines, rest-days,
 * config/revenuecat-key), every one uncredentialed, and made no successful request for
 * the next ten minutes. Downstream that reads as: workouts "removed", feature flags
 * never hydrated, queued completions deleted as permanently rejected, forced re-signin.
 *
 * WHAT THIS GIVES YOU
 * ===================
 * `apiCall` is a thin, deliberately boring wrapper over `fetchWithAuth`:
 *   - it resolves a relative path against the configured API base, so call sites stop
 *     rebuilding URLs by hand;
 *   - it attaches the credential, refreshing proactively and retrying exactly once on a
 *     server 401;
 *   - it NEVER fires a request without a credential, returning a marked synthetic 401
 *     instead (see `isNoCredentialResponse`);
 *   - it returns a plain `Response`, so migrating a call site is a one-line change and
 *     every existing `res.ok` / `res.json()` / status check keeps working.
 *
 * Do NOT set an Authorization header yourself. If you find yourself wanting to, the
 * answer is almost always that you want `fetchWithAuth` directly.
 *
 * The remaining hand-rolled sites are held by a ratchet that only permits the count to
 * fall: tests/gate/test_raw_bearer_ratchet.py.
 */
import { API_BASE_URL } from '@/utils/apiConfig';
import { isNoCredentialResponse } from './authResponse';

/**
 * `fetchWithAuth` is required LAZILY. Its import chain reaches `expo-secure-store`
 * (untransformed ESM), so importing it at module load makes every consumer's jest suite
 * fail to parse — five health suites stopped running the first time this file imported
 * it eagerly. `require` is cached, so the cost is one lookup per process.
 */
type FetchWithAuthFn = (url: string, init?: RequestInit) => Promise<Response>;
let _fetchWithAuth: FetchWithAuthFn | null = null;
function getFetchWithAuth(): FetchWithAuthFn {
  if (!_fetchWithAuth) {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    _fetchWithAuth = require('./fetchWithAuth').fetchWithAuth as FetchWithAuthFn;
  }
  return _fetchWithAuth;
}

/**
 * Re-exported so a call site needs exactly one import to handle the full contract:
 * make the call, and know whether a 401 means "the server rejected you" or "we never
 * asked". Those are different facts and only one of them is evidence about the session.
 */
export { isNoCredentialResponse };

/** Absolute URLs pass through untouched; anything else resolves against the API base. */
export function resolveApiUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const base = (API_BASE_URL || '').replace(/\/+$/, '');
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${base}${path}`;
}

/**
 * Authenticated API call.
 *
 * @param pathOrUrl `/api/goals`, or a full URL for the rare off-base case.
 * @param init      Standard `RequestInit`. Do not set Authorization — this adds it.
 * @returns         A standard `Response`. Network errors throw, exactly like `fetch`.
 *
 * @example
 *   const res = await apiCall('/api/goals');
 *   if (isNoCredentialResponse(res)) return;   // not signed in yet — try again later
 *   if (!res.ok) throw new Error(`HTTP ${res.status}`);
 *   const data = await res.json();
 */
export async function apiCall(pathOrUrl: string, init: RequestInit = {}): Promise<Response> {
  return getFetchWithAuth()(resolveApiUrl(pathOrUrl), init);
}

/**
 * JSON convenience for the common shape. Returns `null` when there was no credential to
 * send, so a boot-time caller can no-op instead of mistaking it for a rejection.
 *
 * Throws on a non-OK response so callers keep explicit control of real error handling.
 */
export async function apiJson<T = unknown>(
  pathOrUrl: string,
  init: RequestInit = {},
): Promise<T | null> {
  const res = await apiCall(pathOrUrl, init);
  if (isNoCredentialResponse(res)) return null;
  if (!res.ok) throw new Error(`HTTP ${res.status} for ${pathOrUrl}`);
  return (await res.json()) as T;
}
